import { FilenameTemplateItem } from '../../..'
import {
  ConcretePlacementDate,
  ConcreteShortName,
} from '../../filenameTemplateItems/instances/concrete'
import {
  ConcreteTestSetId,
  ConcretePlacementIds,
  ConcreteFieldNumbers,
} from '../../filenameTemplateItems/instances/concrete'
import {
  concretes,
  projects,
} from '../../filenameTemplateItems/instances/groups'
import { ClientProjectNumber } from '../../filenameTemplateItems/instances/project'
import { SharePointPacketReportItem } from './SharePointPacketReportItem'
import { SimpleReportItem } from './SimpleReportItem'

export class ConcreteTestSetReportItem<
  Name extends
    | `${
        | 'Concrete Cylinder and IA Sheet'
        | 'Concrete Placement and Core Sheet'} by Concrete Test Set`,
  QueryParameter extends 'cylinder-ia-sheet' | 'placement-core-sheet',
  SampleFilename extends
    | 'concrete-placement-and-core-sheet'
    | 'concrete-cylinder-and-ia-sheet',
  ID extends string = string
> extends SimpleReportItem<
  ID,
  Name,
  'ConcreteTestSet',
  'Concrete Test Set',
  'concreteTestSets',
  QueryParameter,
  'testSetId',
  SampleFilename,
  'globalId'
> {
  constructor({
    description,
    defaultTemplateItem,
    additionalCompatibleTemplateItems,
    ...options
  }: Pick<
    ConcreteTestSetReportItem<Name, QueryParameter, SampleFilename, ID>,
    'id' | 'name' | 'queryParameter' | 'sampleFilename'
  > & {
    description: 'CONCRETE-STRENGTH-RPT' | 'THICKNESS'
    defaultTemplateItem:
      | typeof ConcreteTestSetId
      | typeof ConcreteFieldNumbers
      | typeof ConcretePlacementIds
    additionalCompatibleTemplateItems?: FilenameTemplateItem[]
  }) {
    super({
      ...options,
      key: 'globalId',
      afterClientProjectNumber: [
        { type: 'custom', text: '_' },
        { type: 'templateItem', templateItemId: ConcreteShortName.id },
        { type: 'custom', text: `_${description}_` },
        { type: 'templateItem', templateItemId: ConcretePlacementDate.id },
        { type: 'custom', text: '_' },
        { type: 'templateItem', templateItemId: defaultTemplateItem.id },
      ],
      classType: 'ConcreteTestSet',
      classTypeName: 'Concrete Test Set',
      endpoint: 'concreteTestSets',
      label: 'testSetId',
      compatibleTemplateItems: [
        ConcreteTestSetId,
        ...projects,
        ...concretes,
        ...(additionalCompatibleTemplateItems ?? []),
      ],
    })
  }
}

export class ConcreteTestSetPacketReportItem<
  ID extends string
> extends SharePointPacketReportItem<
  ID,
  'Concrete Test Set',
  'ConcreteTestSet',
  'Concrete Test Set',
  'concreteTestSets',
  'packet',
  'testSetId',
  'concrete-test-set',
  'globalId'
> {
  /**
   * Test Set Packets have at least 2 old filename types we have to consider
   * because we attempt to remove them from SharePoint by name before saving a new one
   *
   * @deprecated
   * @expires after we archive I-80; I-215 and US-89 in the database
   */
  deprecatedTemplateItems = [
    [
      ClientProjectNumber.id,
      '_CONCRETE_STRENGTH_RPT_',
      ConcreteTestSetId.id,
      '_',
      ConcretePlacementDate.id,
    ],
    [ClientProjectNumber.id, '_', ConcreteTestSetId.id],
  ]
  constructor(id: ID) {
    super({
      id,
      key: 'globalId',
      name: 'Concrete Test Set',
      queryParameter: 'packet',
      sampleFilename: 'concrete-test-set',
      afterClientProjectNumber: [
        { type: 'custom', text: '_' },
        { type: 'templateItem', templateItemId: ConcreteShortName.id },
        { type: 'custom', text: '_CONCRETE-STRENGTH-RPT_' },
        { type: 'templateItem', templateItemId: ConcretePlacementDate.id },
        { type: 'custom', text: '_' },
        { type: 'templateItem', templateItemId: ConcreteTestSetId.id },
      ],
      classType: 'ConcreteTestSet',
      classTypeName: 'Concrete Test Set',
      compatibleTemplateItems: [ConcreteTestSetId, ...projects, ...concretes],
      endpoint: 'concreteTestSets',
      label: 'testSetId',
    })
  }
}
